
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { useSupportBubble } from "@/hooks/use-support-bubble";
import { SupportOptionsView } from "@/components/support/SupportOptionsView";
import { EmailOptionsView } from "@/components/support/EmailOptionsView";
import { BillingActions } from "@/components/billing/BillingActions";
import { TutorialTrigger } from "@/components/ui/tutorial-trigger";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useTrialContext } from "@/contexts/TrialContext";
import { useState } from "react";

interface SupportBubbleProps {
  hasLifetime?: boolean;
}

export const SupportBubble = ({ hasLifetime }: SupportBubbleProps) => {
  const [showBillingActions, setShowBillingActions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { hasActiveSubscription } = useSubscriptionStatus();
  const { hasActiveTrial, hasExpiredTrial } = useTrialContext();

  const {
    isOpen,
    setIsOpen,
    showEmailOptions,
    supportEmail,
    handleFeedback,
    handleEmailOptions,
    handleBooking,
    handleCopyEmail,
    handleOpenMailApp,
    handleBack,
  } = useSupportBubble(hasLifetime);

  const handleSubscriptionManagement = () => {
    setShowBillingActions(true);
  };

  const handleBillingBack = () => {
    setShowBillingActions(false);
  };

  const handleBillingSuccess = () => {
    setIsOpen(false);
    setShowBillingActions(false);
  };

  const isTrialUser = (hasActiveTrial || hasExpiredTrial) && !hasActiveSubscription;

  return (
    <div className="fixed bottom-4 right-4 z-50" data-support-bubble>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            className="h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg"
            size="icon"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-4" side="top" align="end">
          {showBillingActions ? (
            <div className="space-y-4">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  className="p-0 h-8 mr-2"
                  onClick={handleBillingBack}
                >
                  ←
                </Button>
                <h3 className="font-semibold text-lg">
                  {isTrialUser ? "Upgrade Subscription" : "Manage Subscription"}
                </h3>
              </div>
              <BillingActions
                onSuccess={handleBillingSuccess}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            </div>
          ) : !showEmailOptions ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">How can I help?</h3>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleFeedback}
              >
                Share Feedback
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleEmailOptions}
              >
                Ask a Question
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleBooking}
              >
                Book Strategy Session
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleSubscriptionManagement}
              >
                {isTrialUser ? "Upgrade Subscription" : "Manage Subscription"}
              </Button>

              <div className="pt-2 border-t">
                <TutorialTrigger 
                  tutorialId="dashboard-overview" 
                  variant="link"
                  size="sm"
                >
                  Tutorial: Dashboard Overview
                </TutorialTrigger>
              </div>
            </div>
          ) : (
            <EmailOptionsView
              supportEmail={supportEmail}
              onBack={handleBack}
              onCopyEmail={handleCopyEmail}
              onOpenMailApp={handleOpenMailApp}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
